import {
  DATA_SETS_NAME_MAX_LENGTH,
  DATA_SETS_NAME_MIN_LENGTH,
} from 'constants/general';
import i18next from 'i18next';
import * as yup from 'yup';

export const createNotEmptyObjectTest = (fieldNameTranslationKey?: string) => ({
  name: 'not-empty',
  message: fieldNameTranslationKey
    ? i18next.t('errors.requiredField', {
      name: i18next.t(fieldNameTranslationKey, { interpolation: { escapeValue: false } }),
      interpolation: { escapeValue: false },
    })
    : i18next.t('errors.requiredField'),
  test: (obj: { label?: string, value?: string } | null | undefined) => {
    if (!obj) return false;
    return Boolean(obj.value) && Boolean(obj.label);
  },
});

export const createLabelValueObjectSchema = (fieldNameTranslationKey: string) => yup.object().shape({
  label: yup.string()
    .required()
    .min(1, i18next.t('errors.requiredField', {
      name: i18next.t(fieldNameTranslationKey),
    })),
  value: yup.string()
    .required()
    .min(1, i18next.t('errors.requiredField', {
      name: i18next.t(fieldNameTranslationKey),
    })),
}).test('not-empty', i18next.t('errors.requiredField', {
  name: i18next.t(fieldNameTranslationKey, { interpolation: { escapeValue: false } }),
  interpolation: { escapeValue: false },
}), (obj) => {
  if (!obj) return false;
  return !(obj.value === '' && obj.label === '');
}).required(i18next.t(
  'errors.requiredField',
  {
    name: i18next.t(fieldNameTranslationKey, { interpolation: { escapeValue: false } }),
    interpolation: { escapeValue: false },
  },
));

export const createCountryValidation = (
  fieldNameKey: string = 'profitCentres.createProfitCentre.fields.country',
) => yup.object().shape({
  label: yup.string(),
  value: yup.string(),
}).test('not-empty', i18next.t('errors.requiredField', {
  name: i18next.t(fieldNameKey, { interpolation: { escapeValue: false } }),
  interpolation: { escapeValue: false },
}), (obj) => {
  if (!obj) return false;
  return !(obj.value === '' && obj.label === '');
}).default(undefined)
  .required(i18next.t(
    'errors.requiredField',
    {
      name: i18next.t(fieldNameKey, { interpolation: { escapeValue: false } }),
      interpolation: { escapeValue: false },
    },
  ));

export const createStateValidation = (
  fieldNameKey: string = 'profitCentres.createProfitCentre.fields.state',
) => createCountryValidation(fieldNameKey);

export const createNaceSectionValidation = (fieldNameKey: string) => yup.object().shape({
  label: yup.string(),
  value: yup.string().required(i18next.t(
    'errors.requiredField',
    { name: i18next.t(fieldNameKey) },
  )),
  divisions: yup.array(),
}).test('not-empty', i18next.t('errors.requiredField', {
  name: i18next.t(fieldNameKey, { interpolation: { escapeValue: false } }),
  interpolation: { escapeValue: false },
}), (obj) => {
  if (!obj) return false;
  return !(obj.value === '' && obj.label === '');
}).default(undefined)
  .required(i18next.t(
    'errors.requiredField',
    {
      name: i18next.t(fieldNameKey, { interpolation: { escapeValue: false } }),
      interpolation: { escapeValue: false },
    },
  ));

export const createStandardObjectValidation = (
  fieldNameKey: string,
  isRequired: boolean = true,
) => {
  let schema = yup.object().shape({
    label: yup.string(),
    value: yup.string(),
  }).test(createNotEmptyObjectTest(fieldNameKey)).default(undefined);

  if (isRequired) {
    schema = schema.required(i18next.t(
      'errors.requiredField',
      {
        name: i18next.t(fieldNameKey, { interpolation: { escapeValue: false } }),
        interpolation: { escapeValue: false },
      },
    ));
  }

  return schema;
};

export const createAddressFieldValidation = (
  fieldType: 'country' | 'state' | 'city',
  customFieldNameKey?: string,
  isRequired: boolean = true,
) => {
  const defaultKeyMap = {
    country: 'profitCentres.createProfitCentre.fields.country',
    state: 'profitCentres.createProfitCentre.fields.state',
    city: 'profitCentres.createProfitCentre.fields.city',
  };

  const fieldNameKey = customFieldNameKey || defaultKeyMap[fieldType];
  return createStandardObjectValidation(fieldNameKey, isRequired);
};

export const createPostCodeValidation = (fieldNameKey: string = 'suppliers.fields.post') => yup.string()
  .trim()
  .max(
    DATA_SETS_NAME_MAX_LENGTH,
    i18next.t('errors.maxCharactersLength', {
      name: i18next.t(fieldNameKey),
      length: DATA_SETS_NAME_MAX_LENGTH,
    }),
  );

export const createStringFieldValidation = ({
  fieldNameKey,
  minLength = DATA_SETS_NAME_MIN_LENGTH,
  maxLength = DATA_SETS_NAME_MAX_LENGTH,
  isRequired = false,
  useRegex = null,
}: {
  fieldNameKey: string,
  minLength?: number,
  maxLength?: number,
  isRequired?: boolean,
  useRegex?: RegExp | null,
}) => {
  let validator = yup.string()
    .test(
      'Min length when not empty value',
      i18next.t('errors.minCharactersLength', { name: i18next.t(fieldNameKey), length: minLength }),
      (value) => !value || value.length >= minLength,
    )
    .max(
      maxLength,
      i18next.t('errors.maxCharactersLength', { name: i18next.t(fieldNameKey), length: maxLength }),
    )
    .transform((value) => value?.trim())
    .trim();

  if (isRequired) {
    validator = validator.required(i18next.t('errors.requiredField', { name: i18next.t(fieldNameKey) }));
  }

  if (useRegex) {
    validator = validator.matches(useRegex, i18next.t('errors.nameContainLettersAndSpecialSymbols'));
  }

  return validator;
};

export function createEuTaxonomyValidation(fieldNameKey = 'ownOperations.fields.taxonomy') {
  return yup.string()
    .test(
      'Min length when not empty value',
      i18next.t('errors.minCharactersLength', { name: i18next.t(fieldNameKey), length: DATA_SETS_NAME_MIN_LENGTH }),
      (value) => !value || value.length >= DATA_SETS_NAME_MIN_LENGTH,
    )
    .max(
      DATA_SETS_NAME_MAX_LENGTH,
      i18next.t('errors.maxCharactersLength', { name: i18next.t(fieldNameKey), length: DATA_SETS_NAME_MAX_LENGTH }),
    )
    .transform((value) => value?.trim())
    .trim();
}

export function createObjectShapeValidation(fieldNameKey: string) {
  return yup.object().shape({
    label: yup.string().required(i18next.t(
      'errors.requiredField',
      {
        name: i18next.t(
          fieldNameKey,
          { interpolation: { escapeValue: false } },
        ),
        interpolation: { escapeValue: false },
      },
    )),
    value: yup.string().required(i18next.t(
      'errors.requiredField',
      {
        name: i18next.t(
          fieldNameKey,
          { interpolation: { escapeValue: false } },
        ),
        interpolation: { escapeValue: false },
      },
    )),
  }).required(i18next.t(
    'errors.requiredField',
    {
      name: i18next.t(
        fieldNameKey,
        { interpolation: { escapeValue: false } },
      ),
      interpolation: { escapeValue: false },
    },
  ));
}
